import { z } from "zod";
import { evidenceMetaSchema } from "./agent-evidence.js";
// The versioned Lafayette UI parts (CHT-715; docs/architecture/phase-4-ui-protocol.md). A part is an AI SDK typed data
// part named `data-lafayette-<name>`. The SERVER builds every part from the tool results of the run: the model never
// writes a part, it only picks a permitted type and ids that the run's tools returned (agent-ui-parts-presentation.ts).
//
// Rules that every schema here follows (a contract test enforces them):
//   - `z.strictObject` at every level: an unknown key is a failure, never a silent pass.
//   - `schemaVersion` is an explicit literal. A new shape is a new version, never a silent edit.
//   - A part holds canonical entity ids and the MINIMUM immutable display snapshot (a title, a short text, a city).
//     Current state is read again through the normal queries. A snapshot is never an authority.
//   - No key can authorize a payment or a state transition: no amount outside the one marked `authoritative: false`, no
//     status as a command, no url, no link, no html, no style, no component name.
//   - Text is plain display text: no angle bracket, no web address, no markdown link, no control character.
//   - Every part has a hard maximum JSON size (`UI_PART_MAX_JSON_BYTES`).
//
// This file depends on `zod` and `agent-evidence.ts` only, because the mobile client imports it.
/** The prefix of the AI SDK data part type. The full type of the `recommendation` part is `data-lafayette-recommendation`. */
export const LAFAYETTE_UI_PART_PREFIX = "data-lafayette-";
/** The parts that the model may ask for (by type). `run-status` is not in the list: only the server writes it. */
export const lafayettePresentableUiPartNames = Object.freeze([
    "recommendation",
    "recommendation-collection",
    "comparison",
    "action-status",
    "concierge-request",
    "handoff-status",
    "itinerary-preview",
    "payment-request-placeholder",
]);
export const lafayetteUiPartNames = Object.freeze([
    ...lafayettePresentableUiPartNames,
    "run-status",
]);
/** The versions of each part that this build of the schemas can read and write. Only `1` exists. */
export const LAFAYETTE_UI_PART_VERSIONS = Object.freeze({
    recommendation: [1],
    "recommendation-collection": [1],
    comparison: [1],
    "action-status": [1],
    "concierge-request": [1],
    "handoff-status": [1],
    "itinerary-preview": [1],
    "payment-request-placeholder": [1],
    "run-status": [1],
});
export const UI_PART_LIMITS = Object.freeze({
    /** The hard maximum of the JSON of ONE part `data` object, in UTF-8 bytes. A larger part is refused, never cut. */
    maxJsonBytes: 16_384,
    /** The most data parts of one response, `run-status` not counted. */
    maxPartsPerResponse: 8,
    collectionItems: { min: 2, max: 8 },
    comparisonItems: { min: 2, max: 4 },
    itinerarySections: 5,
    itineraryItemsPerSection: 4,
    rationaleChars: 200,
});
// --- plain display text ----------------------------------------------------------------------------------------------
/** True for a C0 or C1 control character, DEL, or a Unicode line or paragraph separator. */
function isControlCode(code) {
    return code < 0x20 || (code >= 0x7f && code <= 0x9f) || code === 0x2028 || code === 0x2029;
}
const hasControlCharacter = (value) => {
    for (let index = 0; index < value.length; index += 1) {
        if (isControlCode(value.charCodeAt(index)))
            return true;
    }
    return false;
};
const withoutControlCharacters = (value) => {
    let out = "";
    for (let index = 0; index < value.length; index += 1) {
        out += isControlCode(value.charCodeAt(index)) ? " " : value[index];
    }
    return out;
};
const MARKUP_CHARACTERS = /[<>]/u;
const WEB_ADDRESS = /(?:\b[a-z][a-z0-9+.-]{1,15}:\/\/|\bwww\.|\b(?:javascript|data|mailto|tel|file):|\]\()/iu;
/** True for a one-line text with no markup, no web address, no markdown link and no control character. */
export function isSafeDisplayText(value) {
    return (value.length > 0 &&
        value === value.trim() &&
        !hasControlCharacter(value) &&
        !MARKUP_CHARACTERS.test(value) &&
        !WEB_ADDRESS.test(value));
}
const safeText = (max) => z.string().min(1).max(max).refine(isSafeDisplayText, { message: "must be plain display text" });
/**
 * Makes text from Lafayette data or from a model fit for a part: one line, no control character, no angle bracket, no web
 * address, cut to `max` characters. Returns `null` when nothing is left. The server calls this before it builds a part,
 * so a part never fails on a quirk of catalog text; the schema above stays strict for every other writer.
 */
export function toSafeDisplayText(value, max) {
    if (value === null || value === undefined)
        return null;
    const cleaned = withoutControlCharacters(value)
        .replace(/<[^<>]*>/gu, " ")
        .replace(/[<>]/gu, " ")
        .replace(/\]\(/gu, "] (")
        .replace(/\b[a-z][a-z0-9+.-]{1,15}:\/\/\S*/giu, " ")
        .replace(/\bwww\.\S*/giu, " ")
        .replace(/\b(?:javascript|data|mailto|tel|file):/giu, " ")
        .replace(/\s+/gu, " ")
        .trim();
    if (cleaned === "")
        return null;
    let cut = Array.from(cleaned).slice(0, max).join("").trimEnd();
    // `max` counts UTF-16 units in the schema: shrink until the cut fits.
    while (cut.length > max)
        cut = Array.from(cut).slice(0, -1).join("").trimEnd();
    return cut === "" || !isSafeDisplayText(cut) ? null : cut;
}
// --- shared pieces ---------------------------------------------------------------------------------------------------
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
/** A canonical Lafayette id. */
const idSchema = z.string().regex(UUID_SHAPE);
/** A closed code such as an item type or a status code, as the tools return it. */
const codeSchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,47}$/u);
const isoInstant = z.iso.datetime();
const isoDate = z.iso.date();
const schemaVersion1 = z.literal(1);
/** At most one short sentence of the model about why this part fits. The only model text in a part. */
const rationale = safeText(UI_PART_LIMITS.rationaleChars).nullable();
/** The display snapshot of one published catalog item: title, short text, city. No image, no price, no link. */
const catalogSnapshot = z.strictObject({
    catalogItemId: idSchema,
    itemType: codeSchema,
    title: safeText(120),
    summary: safeText(240).nullable(),
    /** The city or locality. */
    locality: safeText(80).nullable(),
    countryCode: z
        .string()
        .regex(/^[A-Z]{2}$/u)
        .nullable(),
});
/** A catalog entry of a part: the snapshot and the evidence that names the same item. */
const catalogEntry = z
    .strictObject({ item: catalogSnapshot, evidence: evidenceMetaSchema })
    .refine(({ item, evidence }) => evidence.source === "CATALOG_FACT" &&
    evidence.entity?.type === "CATALOG_ITEM" &&
    evidence.entity.id.toLowerCase() === item.catalogItemId.toLowerCase(), { path: ["evidence"], message: "the evidence must name this catalog item" });
const allDistinct = (ids) => new Set(ids.map((id) => id.toLowerCase())).size === ids.length;
// --- the parts, version 1 --------------------------------------------------------------------------------------------
/** One place or offer that Lafayette recommends. Server-built from `searchCatalog` or `getCatalogItem`. */
export const recommendationPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    entry: catalogEntry,
    rationale,
});
/** A short list of recommended items, in the order the model chose. */
export const recommendationCollectionPartV1 = z
    .strictObject({
    schemaVersion: schemaVersion1,
    entries: z
        .array(catalogEntry)
        .min(UI_PART_LIMITS.collectionItems.min)
        .max(UI_PART_LIMITS.collectionItems.max),
    rationale,
})
    .refine((part) => allDistinct(part.entries.map(({ item }) => item.catalogItemId)), {
    path: ["entries"],
    message: "an item appears once",
});
export const comparisonAttributeKeys = Object.freeze(["ITEM_TYPE", "LOCALITY", "COUNTRY"]);
/**
 * Two to four items side by side. The attributes are built by the server from the snapshots (only the ones that differ);
 * the model supplies no cell.
 */
export const comparisonPartV1 = z
    .strictObject({
    schemaVersion: schemaVersion1,
    entries: z
        .array(catalogEntry)
        .min(UI_PART_LIMITS.comparisonItems.min)
        .max(UI_PART_LIMITS.comparisonItems.max),
    attributes: z
        .array(z.strictObject({
        key: z.enum(comparisonAttributeKeys),
        /** One value per entry, in entry order. */
        values: z.array(safeText(80).nullable()).min(2).max(UI_PART_LIMITS.comparisonItems.max),
    }))
        .max(comparisonAttributeKeys.length),
    rationale,
})
    .refine((part) => allDistinct(part.entries.map(({ item }) => item.catalogItemId)), {
    path: ["entries"],
    message: "an item appears once",
})
    .refine((part) => part.attributes.every((attribute) => attribute.values.length === part.entries.length), {
    path: ["attributes"],
    message: "one value per entry",
});
/**
 * The status line of one action of the customer, as it was when the agent read it. `statusCode` is a display code, not a
 * command: no client sends it back, and the real status is read again from the request.
 */
export const actionStatusPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    entity: z.strictObject({ type: z.enum(["CONCIERGE_REQUEST"]), id: idSchema }),
    title: safeText(200),
    statusCode: codeSchema,
    summary: safeText(300).nullable(),
    updatedAt: isoInstant,
    evidence: evidenceMetaSchema,
    rationale,
});
/** A concierge request, as the agent read it. The client opens the live request by `requestId`. */
export const conciergeRequestPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    request: z.strictObject({
        requestId: idSchema,
        requestType: codeSchema,
        title: safeText(200),
        statusCode: codeSchema,
        partySize: z.number().int().min(1).max(999).nullable(),
        dateFrom: isoDate.nullable(),
        dateTo: isoDate.nullable(),
        summary: safeText(300).nullable(),
        pendingQuestion: safeText(300).nullable(),
        updatedAt: isoInstant,
    }),
    evidence: evidenceMetaSchema,
    rationale,
});
/** A handoff to a person was requested in this conversation. The id is the conversation: a handoff has no id of its own yet. */
export const handoffStatusPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    handoff: z.strictObject({
        conversationId: idSchema,
        status: z.enum(["REQUESTED"]),
    }),
    evidence: evidenceMetaSchema,
    rationale,
});
/** A read-only preview of an itinerary: a few sections and a few titles. The client opens the live itinerary by id. */
export const itineraryPreviewPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    itinerary: z.strictObject({
        itineraryId: idSchema,
        tripId: idSchema.nullable(),
        title: safeText(200),
        statusCode: codeSchema,
        /** Items of the whole plan, and how many of them the preview does not show. */
        itemCount: z.number().int().min(0).max(1_000),
        omittedItemCount: z.number().int().min(0).max(1_000),
        sections: z
            .array(z.strictObject({
            title: safeText(120).nullable(),
            localDate: isoDate.nullable(),
            items: z
                .array(z.strictObject({
                itineraryItemId: idSchema,
                kind: codeSchema,
                title: safeText(160),
            }))
                .max(UI_PART_LIMITS.itineraryItemsPerSection),
        }))
            .max(UI_PART_LIMITS.itinerarySections),
    }),
    evidence: evidenceMetaSchema,
    rationale,
});
/**
 * A placeholder that tells the customer a payment request exists. It AUTHORIZES NOTHING: the amount is a display snapshot
 * (`authoritative` is the literal `false`), the part has no action, and the customer pays only in the app, on the payment
 * request that the server reads again. A payment request is never created or changed from a part.
 */
export const paymentRequestPlaceholderPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    paymentRequestId: idSchema,
    conciergeRequestId: idSchema.nullable(),
    displayAmount: z
        .strictObject({
        amountMinor: z.number().int().min(0).max(1_000_000_000_000),
        currency: z.string().regex(/^[A-Z]{3}$/u),
        authoritative: z.literal(false),
    })
        .nullable(),
    evidence: evidenceMetaSchema,
    rationale,
});
/** The outcome of a run, as design 2.7 (`AgentRunOutcome`) defines it. The client shows a fixed text for `safeTextKey`. */
export const runStatusDegradedReasons = Object.freeze([
    "RUNTIME_OFF",
    "RUNTIME_START_FAILED",
    "MODEL_TIMEOUT",
    "MODEL_UNAVAILABLE",
    "MODEL_RATE_LIMITED",
    "MODEL_QUOTA_EXHAUSTED",
    "MODEL_REFUSED",
    "MODEL_BAD_OUTPUT",
    "RUN_DEADLINE_EXCEEDED",
    "STEP_LIMIT_REACHED",
    "FENCED",
    "CANCELLED",
]);
export const runStatusSafeTextKeys = Object.freeze([
    "agent.unavailable",
    "agent.slow",
    "agent.busy",
    "agent.cannot_answer",
    "agent.with_person",
    "agent.cancelled",
]);
export const runStatusSuggestedActions = Object.freeze([
    "RETRY_LATER",
    "ASK_FOR_CONCIERGE",
    "NONE",
]);
export const runOutcomeSchema = z.discriminatedUnion("status", [
    z.strictObject({ status: z.literal("COMPLETED") }),
    z.strictObject({
        status: z.literal("DEGRADED"),
        reason: z.enum(runStatusDegradedReasons),
        suggestedAction: z.enum(runStatusSuggestedActions),
        safeTextKey: z.enum(runStatusSafeTextKeys),
    }),
]);
/**
 * The LAST data part of every stream. It carries the run id (the durable response id), the outcome, and the number of
 * data parts that came before it, so a client can tell a complete stream from a cut one.
 */
export const runStatusPartV1 = z.strictObject({
    schemaVersion: schemaVersion1,
    runId: idSchema,
    outcome: runOutcomeSchema,
    partCount: z.number().int().min(0).max(UI_PART_LIMITS.maxPartsPerResponse),
});
// --- registry and typed access ---------------------------------------------------------------------------------------
/** The schema of each part, by name and version. */
export const LAFAYETTE_UI_PART_SCHEMAS = Object.freeze({
    recommendation: { 1: recommendationPartV1 },
    "recommendation-collection": { 1: recommendationCollectionPartV1 },
    comparison: { 1: comparisonPartV1 },
    "action-status": { 1: actionStatusPartV1 },
    "concierge-request": { 1: conciergeRequestPartV1 },
    "handoff-status": { 1: handoffStatusPartV1 },
    "itinerary-preview": { 1: itineraryPreviewPartV1 },
    "payment-request-placeholder": { 1: paymentRequestPlaceholderPartV1 },
    "run-status": { 1: runStatusPartV1 },
});
/** The AI SDK data part type of a part name: `data-lafayette-recommendation`. */
export function lafayetteUiPartType(name) {
    return `${LAFAYETTE_UI_PART_PREFIX}${name}`;
}
const encoder = new TextEncoder();
/** The size of the JSON of a value in UTF-8 bytes, or `null` when the value cannot be serialized (a cycle, a bigint). */
export function jsonByteLength(value) {
    try {
        const json = JSON.stringify(value);
        return json === undefined ? null : encoder.encode(json).length;
    }
    catch {
        return null;
    }
}
// --- parse with the fallback states ----------------------------------------------------------------------------------
export const uiPartUnsupportedReasons = Object.freeze([
    "UNKNOWN_PART",
    "UNSUPPORTED_VERSION",
]);
export const uiPartInvalidReasons = Object.freeze(["MALFORMED", "OVERSIZED"]);
const NAME_PATTERN = /^[a-z][a-z0-9-]{0,47}$/u;
function partNameOf(type) {
    if (!type.startsWith(LAFAYETTE_UI_PART_PREFIX))
        return null;
    const name = type.slice(LAFAYETTE_UI_PART_PREFIX.length);
    if (!NAME_PATTERN.test(name))
        return "UNKNOWN";
    return lafayetteUiPartNames.includes(name)
        ? name
        : "UNKNOWN";
}
/**
 * Reads the `type` and the `data` of one AI SDK data chunk. Order: name, size, version, shape. The size check comes before
 * the shape check, so a client never walks a huge value.
 */
export function parseLafayetteUiPart(type, data) {
    const name = partNameOf(type);
    if (name === null || name === "UNKNOWN") {
        return { status: "unsupported", reason: "UNKNOWN_PART", name: null, version: null };
    }
    const bytes = jsonByteLength(data);
    if (bytes === null || bytes > UI_PART_LIMITS.maxJsonBytes) {
        return { status: "invalid", reason: "OVERSIZED", name };
    }
    const version = typeof data === "object" && data !== null && !Array.isArray(data)
        ? data.schemaVersion
        : undefined;
    if (typeof version !== "number" || !Number.isInteger(version)) {
        return { status: "invalid", reason: "MALFORMED", name };
    }
    const known = LAFAYETTE_UI_PART_VERSIONS[name];
    if (!known.includes(version)) {
        return { status: "unsupported", reason: "UNSUPPORTED_VERSION", name, version };
    }
    const schema = LAFAYETTE_UI_PART_SCHEMAS[name][version];
    const parsed = schema?.safeParse(data);
    if (parsed === undefined || !parsed.success) {
        return { status: "invalid", reason: "MALFORMED", name };
    }
    return { status: "ok", part: { name, data: parsed.data } };
}
// --- version negotiation ---------------------------------------------------------------------------------------------
/**
 * What the client can render: part names and the versions it reads. The client sends it with the run request
 * (`uiParts`). The server never sends a part version that the client did not declare: it sends safe text instead.
 * Names that this server does not know are ignored, so a newer client can declare more.
 */
export const uiPartSupportSchema = z
    .record(z.string().regex(NAME_PATTERN), z.array(z.number().int().min(1).max(1_000)).max(8))
    .refine((support) => Object.keys(support).length <= 32, { message: "too many part names" });
/** What a client of this build declares. */
export const LAFAYETTE_UI_PART_SUPPORT = Object.freeze(Object.fromEntries(lafayetteUiPartNames.map((name) => [name, [...LAFAYETTE_UI_PART_VERSIONS[name]]])));
/**
 * The version the server may send of a part: the highest version that both sides know, or `null` (send the safe text).
 * `run-status` is the one exception, and it is a rule of the protocol, not of this function: the server always sends the
 * terminal `run-status` of version 1.
 */
export function negotiatedUiPartVersion(support, name) {
    const declared = support?.[name];
    if (declared === undefined)
        return null;
    const common = LAFAYETTE_UI_PART_VERSIONS[name].filter((version) => declared.includes(version));
    return common.length === 0 ? null : Math.max(...common);
}
// --- safe text for a part that the client cannot read ----------------------------------------------------------------
/**
 * One short plain line that says what a part would have shown. The server sends it as ordinary text when the client did not
 * declare the part, so no information is lost and nothing needs a component. It is built from the validated part only.
 */
export function lafayetteUiPartFallbackText(part) {
    const line = (item) => item.locality === null ? item.title : `${item.title} (${item.locality})`;
    switch (part.name) {
        case "recommendation":
            return line(part.data.entry.item);
        case "recommendation-collection":
        case "comparison":
            return part.data.entries.map(({ item }) => line(item)).join("; ");
        case "action-status":
            return `${part.data.title}: ${part.data.statusCode}`;
        case "concierge-request":
            return `${part.data.request.title}: ${part.data.request.statusCode}`;
        case "itinerary-preview":
            return `${part.data.itinerary.title}: ${part.data.itinerary.itemCount} items`;
        case "handoff-status":
        case "payment-request-placeholder":
        case "run-status":
            // These have a fixed text of the client, or a safe text key. The server adds no line of its own.
            return null;
    }
}
